/**
 * Camera Viewer Component
 * Displays live camera streams and thumbnails with controls
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Videocam as RecordIcon,
  StopCircle as StopRecordIcon,
  Fullscreen as FullscreenIcon,
  PhotoCamera as PhotoIcon,
  Download as DownloadIcon,
  Error as ErrorIcon,
  CheckCircle as OnlineIcon,
  Warning as OfflineIcon
} from '@mui/icons-material';
import { IPCamera, cameraService } from '../../services/cameraService';
import { thumbnailRequestManager } from '../../services/thumbnailRequestManager';

interface StreamTimeoutConfig {
  timeoutMinutes: number;
  heartbeatInterval: number;
  enablePageVisibility: boolean;
  enableStreamTimeout: boolean;
}

interface CameraViewerProps {
  camera: IPCamera;
  initialQuality?: string;
  autoRefreshInterval?: number;
  showControls?: boolean;
  onError?: (error: string) => void;
  onRecordingStart?: () => void;
  onRecordingStop?: () => void;
  streamTimeoutConfig?: StreamTimeoutConfig;
  initialLoadDelay?: number; // Delay in milliseconds before initial thumbnail load
}

export const CameraViewer: React.FC<CameraViewerProps> = ({
  camera,
  initialQuality = 'medium',
  autoRefreshInterval = 10,
  showControls = true,
  onError,
  onRecordingStart,
  onRecordingStop,
  streamTimeoutConfig = {
    timeoutMinutes: 1,
    heartbeatInterval: 10,
    enablePageVisibility: true,
    enableStreamTimeout: true
  },
  initialLoadDelay = 0
}) => {
  const [isLive, setIsLive] = useState(false);
  const [quality, setQuality] = useState(initialQuality);
  const [isRecording, setIsRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(autoRefreshInterval);
  const [thumbnailInfo, setThumbnailInfo] = useState<any>(null);
  const [lastThumbnailUpdate, setLastThumbnailUpdate] = useState<Date | null>(null);
  
  // New timeout-related state
  const [streamId, setStreamId] = useState<string | null>(null);
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);
  const [streamTimeRemaining, setStreamTimeRemaining] = useState(streamTimeoutConfig.timeoutMinutes * 60);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  
  // Dialog states
  const [recordingDialogOpen, setRecordingDialogOpen] = useState(false);
  const [recordingNotes, setRecordingNotes] = useState('');
  const [fullSnapshotOpen, setFullSnapshotOpen] = useState(false);
  const [fullSnapshotLoading, setFullSnapshotLoading] = useState(false);
  const [fullSnapshotUrl, setFullSnapshotUrl] = useState<string | null>(null);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [useImageStream, setUseImageStream] = useState(false);
  const [imageStreamSrc, setImageStreamSrc] = useState<string | null>(null);
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageStreamRef = useRef<HTMLImageElement>(null);
  const intervalRef = useRef<number | null>(null);
  const thumbnailRef = useRef<HTMLImageElement>(null);
  const fullImageRef = useRef<HTMLImageElement>(null);
  
  // Refs for timeout intervals
  const heartbeatIntervalRef = useRef<number | null>(null);
  const timeoutCountdownRef = useRef<number | null>(null);
  const visibilityTimeoutRef = useRef<number | null>(null);
  
  // Generate unique client ID
  const clientId = useRef(crypto.randomUUID()).current;
  
  useEffect(() => {
    // Load initial thumbnail info
    loadThumbnailInfo();
    
    if (!isLive && autoRefreshInterval > 0) {
      startThumbnailRefresh();
    }
    
    return () => {
      stopThumbnailRefresh();
      // Clean up video element when component unmounts
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
        videoRef.current.onerror = null;
      }
    };
  }, [isLive, refreshInterval]);
  
  // Page Visibility Detection
  useEffect(() => {
    if (!streamTimeoutConfig.enablePageVisibility) return;
    
    const handleVisibilityChange = () => {
      const wasVisible = isPageVisible;
      const nowVisible = !document.hidden;
      
      setIsPageVisible(nowVisible);
      
      if (isLive) {
        if (!nowVisible && wasVisible) {
          // Page became hidden - start countdown to pause stream
          console.log('📱 Page hidden, starting visibility timeout...');
          visibilityTimeoutRef.current = window.setTimeout(() => {
            console.log('⏸️ Pausing stream due to page being hidden');
            handleStopLiveStream();
            setError('Stream paused - page was not visible for too long');
          }, 30000); // 30 second delay before pausing
          
        } else if (nowVisible && !wasVisible) {
          // Page became visible again - cancel countdown
          console.log('👁️ Page visible again, canceling timeout');
          if (visibilityTimeoutRef.current) {
            clearTimeout(visibilityTimeoutRef.current);
            visibilityTimeoutRef.current = null;
          }
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }
    };
  }, [isPageVisible, isLive]);
  
  // Stream Timeout Countdown
  useEffect(() => {
    if (!streamTimeoutConfig.enableStreamTimeout || !isLive) return;
    
    const startCountdown = () => {
      setStreamTimeRemaining(streamTimeoutConfig.timeoutMinutes * 60);
      
      timeoutCountdownRef.current = window.setInterval(() => {
        setStreamTimeRemaining(prev => {
          const newTime = prev - 1;
          
          // Show warning in last 30 seconds
          if (newTime <= 30 && !showTimeoutWarning) {
            setShowTimeoutWarning(true);
          }
          
          // Auto-stop stream when time runs out
          if (newTime <= 0) {
            console.log('⏱️ Stream timeout reached, stopping stream');
            handleStopLiveStream();
            setError('Stream automatically stopped after 1 minute of inactivity');
            return 0;
          }
          
          return newTime;
        });
      }, 1000);
    };
    
    startCountdown();
    
    return () => {
      if (timeoutCountdownRef.current) {
        clearInterval(timeoutCountdownRef.current);
        timeoutCountdownRef.current = null;
      }
    };
  }, [isLive]);
  
  // Heartbeat to keep stream alive
  const sendHeartbeat = useCallback(async () => {
    if (!streamId || !isLive) return;
    
    try {
      const heartbeatUrl = `/api/v1/sites/api/cameras/stream/${streamId}/heartbeat`;
      console.log('💓 Sending heartbeat to:', heartbeatUrl);
      console.log('💓 Using stream ID:', streamId);
      
      const response = await fetch(heartbeatUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('💓 Heartbeat response status:', response.status);
      
      if (response.ok) {
        // Reset countdown on successful heartbeat
        setStreamTimeRemaining(streamTimeoutConfig.timeoutMinutes * 60);
        setShowTimeoutWarning(false);
        console.log('💓 Stream heartbeat sent successfully');
      } else {
        const responseText = await response.text();
        console.warn('⚠️ Heartbeat failed:', response.status, responseText);
        console.warn('⚠️ Heartbeat failed, stream may timeout');
      }
    } catch (error) {
      console.error('❌ Heartbeat error:', error);
    }
  }, [streamId, isLive]);
  
  // Start heartbeat interval
  useEffect(() => {
    if (!isLive || !streamTimeoutConfig.enableStreamTimeout) return;
    
    // Send heartbeat every 10 seconds
    heartbeatIntervalRef.current = window.setInterval(
      sendHeartbeat, 
      streamTimeoutConfig.heartbeatInterval * 1000
    );
    
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [isLive, sendHeartbeat]);
  
  const loadThumbnailInfo = async () => {
    try {
      const info = await cameraService.getThumbnailInfo(camera.id);
      setThumbnailInfo(info);
      if (info?.received_time) {
        try {
          setLastThumbnailUpdate(new Date(info.received_time));
        } catch (_) {
          // ignore parse errors
        }
      }
    } catch (err) {
      console.warn('Failed to load thumbnail info:', err);
    }
  };
  
  const startThumbnailRefresh = () => {
    stopThumbnailRefresh();
    
    if (refreshInterval > 0) {
      intervalRef.current = window.setInterval(() => {
        refreshThumbnail();
      }, refreshInterval * 1000);
    }
  };
  
  const stopThumbnailRefresh = () => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };
  
  const refreshThumbnail = async () => {
    try {
      setThumbnailError(false);
      
      // Use thumbnail request manager to throttle requests and prevent NS_BINDING_ABORTED errors
      if (thumbnailRef.current) {
        try {
          const thumbnailUrl = await thumbnailRequestManager.requestThumbnail(
            camera.id, 
            camera.thumbnail_url
          );
          // Double-check ref is still valid before setting src
          if (thumbnailRef.current) {
            thumbnailRef.current.src = thumbnailUrl;
            setLastThumbnailUpdate(new Date());
          }
        } catch (requestErr) {
          console.warn('Thumbnail request failed, using fallback:', requestErr);
          // Fallback to direct URL if request manager fails
          if (thumbnailRef.current) {
            thumbnailRef.current.src = `${camera.thumbnail_url}?t=${Date.now()}`;
          }
        }
      }
      
      // Update thumbnail info (with delay to avoid overwhelming the server)
      setTimeout(async () => {
        try {
          await loadThumbnailInfo();
        } catch (err) {
          console.warn('Failed to load thumbnail info:', err);
        }
      }, 500);
      
    } catch (err) {
      console.error('Failed to refresh thumbnail:', err);
      setThumbnailError(true);
    }
  };

  const captureNewThumbnail = async () => {
    try {
      setThumbnailError(false);
      setLoading(true);
      
      // Trigger manual thumbnail capture
      const result = await cameraService.captureThumbnail(camera.id);
      
      if (result.success) {
        // Wait a moment for the thumbnail to be processed, then refresh
        setTimeout(async () => {
          if (thumbnailRef.current) {
            try {
              // Clear cache for this camera to force fresh request
              thumbnailRequestManager.clearCache(camera.id);
              const thumbnailUrl = await thumbnailRequestManager.requestThumbnail(
                camera.id, 
                camera.thumbnail_url
              );
              // Double-check ref is still valid before setting src
              if (thumbnailRef.current) {
                thumbnailRef.current.src = thumbnailUrl;
                setLastThumbnailUpdate(new Date());
              }
            } catch (err) {
              console.warn('Failed to refresh after capture:', err);
              // Fallback to direct URL
              if (thumbnailRef.current) {
                thumbnailRef.current.src = `${camera.thumbnail_url}?t=${Date.now()}`;
                setLastThumbnailUpdate(new Date());
              }
            }
          }
        }, 2000);
        
        // Update thumbnail info
        await loadThumbnailInfo();
      } else {
        setError(result.message || 'Failed to capture thumbnail');
        setThumbnailError(true);
      }
    } catch (err) {
      console.error('Failed to capture thumbnail:', err);
      setError('Failed to capture thumbnail');
      setThumbnailError(true);
    } finally {
      setLoading(false);
    }
  };
  
  const handleStartLiveStream = async () => {
    try {
      console.log('🎥 Starting live stream for camera:', camera.id);
      setLoading(true);
      setError(null);
      setShowTimeoutWarning(false);
      
      // Generate stream URL with client ID for timeout tracking
      const streamUrl = `${cameraService.getCameraStreamUrl(camera.id, quality)}&client_id=${clientId}`;
      const mjpegUrl = cameraService.getCameraDirectStreamUrl(camera.id);
      
      console.log('🔗 Generated stream URL:', streamUrl);
      console.log('🔗 MJPEG fallback URL:', mjpegUrl);
      
      // Generate stream ID the same way the backend does (using seconds, not milliseconds)
      const backendStreamId = `${camera.id}_${clientId}_${Math.floor(Date.now() / 1000)}`;
      console.log('🆔 Generated backend-compatible stream ID:', backendStreamId);
      setStreamId(backendStreamId);
      
      setIsLive(true);
      
      // MJPEG streams must use img element, not video element
      // HTML5 video cannot handle multipart/x-mixed-replace content type
      console.log('🎬 Using MJPEG image stream instead of video element');
      
      // For now, skip the separate registration and rely on the image element request
      // The image element will make the same request and register the stream
      console.log('🔗 Stream will be registered when image element loads:', streamUrl);
      console.log('🔗 Expected stream ID for heartbeats:', backendStreamId);
      
      setVideoSrc(null);
      setUseImageStream(true);
      setImageStreamSrc(streamUrl);
      
      // MJPEG streams will be handled by the image element useEffect
      // No need to set up video element handlers for MJPEG streams
      
      if (!useImageStream) {
        // Only set up video element for non-MJPEG streams
        setTimeout(() => {
          const el = videoRef.current;
          if (!el) return;
          
          // Debug: Check what src is actually set on the video element
          console.log('🎬 Video element src attribute:', el.src);
          console.log('🎬 Video element getAttribute src:', el.getAttribute('src'));
          
          // Clear any old handlers
        el.onerror = null;
        el.onloadeddata = null;
        el.oncanplay = async () => {
          try {
            await el.play();
            stopThumbnailRefresh();
            console.log('✅ Live stream started successfully');
          } catch (err) {
            console.error('❌ Video play error:', err);
            // Fallback to MJPEG image stream
            setUseImageStream(true);
            setImageStreamSrc(mjpegUrl);
            stopThumbnailRefresh();
          }
        };
        el.onerror = () => {
          // If video fails to load, fallback to MJPEG image stream
          setUseImageStream(true);
          setImageStreamSrc(mjpegUrl);
          stopThumbnailRefresh();
        };
      }, 0);
      }
      
    } catch (err) {
      console.error('❌ Live stream error:', err);
      const errorMessage = 'Failed to start live stream';
      setError(errorMessage);
      onError?.(errorMessage);
      setIsLive(false);
    } finally {
      setLoading(false);
    }
  };
  
  const handleStopLiveStream = () => {
    // Clean up timeout intervals
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    if (timeoutCountdownRef.current) {
      clearInterval(timeoutCountdownRef.current);
      timeoutCountdownRef.current = null;
    }
    
    if (visibilityTimeoutRef.current) {
      clearTimeout(visibilityTimeoutRef.current);
      visibilityTimeoutRef.current = null;
    }
    
    // Stop stream on backend if we have stream ID
    if (streamId) {
      fetch(`/api/cameras/stream/${streamId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }).catch(err => console.warn('Failed to stop stream on backend:', err));
    }
    
    // Clean up video element
    const el = videoRef.current;
    if (el) {
      // Remove handlers before stopping
      el.onerror = null;
      el.onloadeddata = null;
      el.oncanplay = null;
      el.pause();
      // Properly clear the source to avoid invalid URI errors
      el.removeAttribute('src');
      el.load();
    }
    
    // Reset state
    setIsLive(false);
    setVideoSrc(null);
    setUseImageStream(false);
    setImageStreamSrc(null);
    setStreamId(null);
    setShowTimeoutWarning(false);
    setStreamTimeRemaining(streamTimeoutConfig.timeoutMinutes * 60);
    
    // Restart thumbnail refresh
    startThumbnailRefresh();
  };
  
  // Keep stream alive button
  const handleKeepStreamAlive = () => {
    setStreamTimeRemaining(streamTimeoutConfig.timeoutMinutes * 60);
    setShowTimeoutWarning(false);
    sendHeartbeat();
  };
  
  // Format time remaining
  const formatTimeRemaining = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleStartRecording = async () => {
    try {
      setLoading(true);
      const result = await cameraService.startRecording(camera.id, {
        recording_type: 'manual',
        quality: quality,
        notes: recordingNotes
      });
      
      if (result.success) {
        setIsRecording(true);
        setRecordingDialogOpen(false);
        setRecordingNotes('');
        onRecordingStart?.();
      } else {
        throw new Error(result.message);
      }
      
    } catch (err) {
      const errorMessage = 'Failed to start recording';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleStopRecording = () => {
    setIsRecording(false);
    onRecordingStop?.();
  };
  
  const handleQualityChange = (newQuality: string) => {
    setQuality(newQuality);
    
    // If live streaming, restart with new quality
    if (isLive) {
      handleStopLiveStream();
      setTimeout(() => {
        setQuality(newQuality);
        handleStartLiveStream();
      }, 100);
    }
  };
  
  const handleTestConnection = async () => {
    try {
      setLoading(true);
      const result = await cameraService.testCameraConnection(camera.id);
      
      if (result.success) {
        setError(null);
        alert(`Connection successful! Resolution: ${result.resolution || 'Unknown'}`);
      } else {
        setError(result.message);
      }
      
    } catch (err) {
      setError('Connection test failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFullSnapshot = async () => {
    try {
      setFullSnapshotLoading(true);
      setFullSnapshotOpen(true);
      // Always open modal immediately and then set URL to kick off load
      // Use current timestamp to avoid stale cache
      const ts = Date.now();
      setFullSnapshotLoading(true);
      setFullSnapshotUrl(`${cameraService.getCameraFullImageUrl(camera.id)}?t=${ts}`);
    } catch (err) {
      console.error('Full snapshot error:', err);
      setError('Failed to open full snapshot');
    }
  };

  // When a new thumbnail arrives and the full image modal is open, update the full image URL
  useEffect(() => {
    if (!lastThumbnailUpdate) return;
    if (!fullSnapshotOpen) return;
    const ts = lastThumbnailUpdate.getTime();
    // Keep URL in sync with Django endpoint
    const nextUrl = `${cameraService.getCameraFullImageUrl(camera.id)}?t=${ts}`;
    // Only update if changed to avoid unnecessary reloads
    if (fullSnapshotUrl !== nextUrl) {
      setFullSnapshotLoading(true);
      setFullSnapshotUrl(nextUrl);
    }
  }, [lastThumbnailUpdate, fullSnapshotOpen]);

  // Initial thumbnail loading with request manager and staggered delay
  useEffect(() => {
    const loadInitialThumbnail = async () => {
      if (thumbnailRef.current && !isLive) {
        try {
          const thumbnailUrl = await thumbnailRequestManager.requestThumbnail(
            camera.id, 
            camera.thumbnail_url
          );
          // Double-check ref is still valid before setting src
          if (thumbnailRef.current) {
            thumbnailRef.current.src = thumbnailUrl;
          }
        } catch (err) {
          console.warn('Failed to load initial thumbnail:', err);
          // Fallback to direct URL with null check
          if (thumbnailRef.current) {
            thumbnailRef.current.src = `${camera.thumbnail_url}?t=${Date.now()}`;
          }
        }
      }
    };

    // Apply staggered delay to prevent overwhelming the camera service
    const timer = setTimeout(loadInitialThumbnail, initialLoadDelay);
    return () => clearTimeout(timer);
  }, [camera.id, camera.thumbnail_url, isLive, initialLoadDelay]);

  // Set video src programmatically to avoid browser URL normalization issues
  useEffect(() => {
    const el = videoRef.current;
    if (el && videoSrc && isLive && !useImageStream) {
      console.log('🎬 Setting video element src programmatically:', videoSrc);
      el.src = videoSrc;
    }
  }, [videoSrc, isLive, useImageStream]);

  // Set image stream src programmatically for MJPEG streams
  useEffect(() => {
    const el = imageStreamRef.current;
    if (el && imageStreamSrc && isLive && useImageStream) {
      console.log('🎬 Setting image stream src programmatically:', imageStreamSrc);
      el.src = imageStreamSrc;
      
      // The stream ID was already set in handleStartLiveStream
      // Just log it for debugging
      console.log('🆔 Stream ID already set for heartbeats:', streamId);
    }
  }, [imageStreamSrc, isLive, useImageStream]);

  const handleFullscreen = () => {
    const targetEl = useImageStream ? imageStreamRef.current : videoRef.current;
    if (!targetEl) return;
    const anyEl = targetEl as any;
    if (anyEl.requestFullscreen) return anyEl.requestFullscreen();
    if (anyEl.webkitRequestFullscreen) return anyEl.webkitRequestFullscreen();
    if (anyEl.mozRequestFullScreen) return anyEl.mozRequestFullScreen();
    if (anyEl.msRequestFullscreen) return anyEl.msRequestFullscreen();
  };
  
  const getStatusIcon = () => {
    switch (camera.connection_status) {
      case 'online':
        return <OnlineIcon sx={{ color: 'success.main' }} />;
      case 'offline':
        return <OfflineIcon sx={{ color: 'warning.main' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      default:
        return <OfflineIcon sx={{ color: 'text.disabled' }} />;
    }
  };
  
  const getStatusColor = () => {
    switch (camera.connection_status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };
  
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Simple status indicator: green tick for current snapshot, red cross for no snapshot */}
            {thumbnailInfo?.has_thumbnail ? (
              <Box sx={{ 
                width: 16, 
                height: 16, 
                bgcolor: 'success.main', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px'
              }}>
                ✓
              </Box>
            ) : (
              <Box sx={{ 
                width: 16, 
                height: 16, 
                bgcolor: 'error.main', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px'
              }}>
                ✗
              </Box>
            )}
            <Typography variant="h6">{camera.name}</Typography>
          </Box>
        }
        subheader={
          <Box>
            <Typography variant="body2" color="text.secondary">
              {camera.description}
            </Typography>
            {camera.location_name && (
              <Typography variant="caption" color="text.secondary">
                📍 {camera.location_name}
              </Typography>
            )}
          </Box>
        }
        action={null}
      />
      
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        {/* Page Visibility Warning */}
        {!isPageVisible && isLive && streamTimeoutConfig.enablePageVisibility && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Stream Paused</AlertTitle>
            Camera stream will pause if page remains hidden for too long to save bandwidth.
          </Alert>
        )}
        
        {/* Stream Timeout Warning */}
        {showTimeoutWarning && isLive && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleKeepStreamAlive}
              >
                Keep Alive
              </Button>
            }
          >
            <AlertTitle>Stream Timeout Warning</AlertTitle>
            Stream will automatically stop in {formatTimeRemaining(streamTimeRemaining)} unless you interact with it.
          </Alert>
        )}
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              error.includes('Live stream format not supported') ? (
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => {
                    setError(null);
                    setIsLive(false);
                    startThumbnailRefresh();
                  }}
                >
                  Use Thumbnail Mode
                </Button>
              ) : null
            }
          >
            <AlertTitle>Camera Error</AlertTitle>
            {error}
            {error.includes('Live stream format not supported') && (
              <Box sx={{ mt: 1, fontSize: '0.875rem' }}>
                💡 <strong>Suggestion:</strong> Use thumbnail mode for static images or contact your administrator to configure MP4/WebM streaming.
              </Box>
            )}
          </Alert>
        )}
        
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 300,
            bgcolor: 'black',
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isLive ? (
            useImageStream && imageStreamSrc ? (
              <img
                ref={imageStreamRef}
                src={imageStreamSrc}
                alt={camera.name}
                style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'pointer' }}
                onDoubleClick={handleFullscreen}
              />
            ) : (
              <video
                ref={videoRef}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
                controls
                playsInline
                preload="none"
              />
            )
          ) : (
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <img
                ref={thumbnailRef}
                src="" // Will be set by useEffect with request manager
                alt={camera.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  cursor: 'pointer'
                }}
                onError={() => setThumbnailError(true)}
                onLoad={() => setThumbnailError(false)}
                onClick={handleFullSnapshot}
                title="Click for full resolution snapshot"
              />
              
              {thumbnailError && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.800',
                    color: 'white'
                  }}
                >
                  <Typography variant="body2">Camera Unavailable</Typography>
                </Box>
              )}
              
              {!thumbnailError && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.8)',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem'
                  }}
                >
                  {lastThumbnailUpdate ? (
                    lastThumbnailUpdate.toLocaleTimeString()
                  ) : (
                    'No snapshot'
                  )}
                </Box>
              )}
            </Box>
          )}
          
          {/* Stream timeout indicator */}
          {isLive && streamTimeoutConfig.enableStreamTimeout && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: showTimeoutWarning ? 'error.main' : 'rgba(0,0,0,0.8)',
                color: 'white',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              {showTimeoutWarning ? '⚠️' : '⏱️'} {formatTimeRemaining(streamTimeRemaining)}
            </Box>
          )}
          
          {/* Page visibility indicator */}
          {!isPageVisible && isLive && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                bgcolor: 'warning.main',
                color: 'white',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem'
              }}
            >
              📱 Page Hidden
            </Box>
          )}
          
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      </CardContent>
      
      {showControls && (
        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isLive ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayIcon />}
                onClick={handleStartLiveStream}
                disabled={loading || camera.connection_status !== 'online'}
                sx={{ 
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                }}
              >
                🎥 Live Stream
              </Button>
            ) : (
              <Button
                variant="contained"
                color="error"
                startIcon={<StopIcon />}
                onClick={handleStopLiveStream}
                disabled={loading}
                sx={{
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)' },
                    '70%': { boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' }
                  }
                }}
              >
                🔴 Stop Live
              </Button>
            )}
            
            {!isRecording ? (
              <Button
                variant="outlined"
                startIcon={<RecordIcon />}
                onClick={() => setRecordingDialogOpen(true)}
                disabled={loading || camera.connection_status !== 'online'}
              >
                Record
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="error"
                startIcon={<StopRecordIcon />}
                onClick={handleStopRecording}
              >
                Stop Recording
              </Button>
            )}
          </Box>
          
          {/* Stream status info */}
          {isLive && (
            <Chip
              label={`Live • ${formatTimeRemaining(streamTimeRemaining)} left`}
              color={showTimeoutWarning ? "warning" : "success"}
              size="small"
              sx={{ ml: 1 }}
            />
          )}
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={handleFullSnapshot}
              disabled={loading}
              title="Full resolution snapshot"
            >
              <PhotoIcon />
            </IconButton>
            
            {isLive && (
              <IconButton
                onClick={handleFullscreen}
                title="Fullscreen"
              >
                <FullscreenIcon />
              </IconButton>
            )}
          </Box>
        </CardActions>
      )}
      

      
      {/* Recording Dialog */}
      <Dialog open={recordingDialogOpen} onClose={() => setRecordingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start Recording</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Recording Notes (Optional)"
            value={recordingNotes}
            onChange={(e) => setRecordingNotes(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Enter notes about this recording session..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordingDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleStartRecording} 
            variant="contained"
            disabled={loading}
          >
            Start Recording
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Resolution Snapshot Modal */}
      <Dialog
        open={fullSnapshotOpen}
        onClose={() => setFullSnapshotOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Full Resolution Snapshot - {camera.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Box sx={{ position: 'relative', width: '100%', textAlign: 'center' }}>
              <img
                ref={fullImageRef}
                key={fullSnapshotUrl || 'no-url'}
                src={fullSnapshotUrl || ''}
                alt={`Full resolution snapshot of ${camera.name}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                  visibility: fullSnapshotLoading ? 'hidden' : 'visible'
                }}
                onLoad={() => setFullSnapshotLoading(false)}
                onError={() => {
                  setFullSnapshotLoading(false);
                  // Auto-retry once after a short delay in case cache just populated
                  setTimeout(() => {
                    const retryTs = Date.now();
                    setFullSnapshotLoading(true);
                    setFullSnapshotUrl(`${cameraService.getCameraFullImageUrl(camera.id)}?t=${retryTs}`);
                  }, 800);
                }}
              />
              {fullSnapshotLoading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullSnapshotOpen(false)}>Close</Button>
          <Button
            onClick={() => {
              const link = document.createElement('a');
              const ts = lastThumbnailUpdate ? lastThumbnailUpdate.getTime() : Date.now();
              const href = fullSnapshotUrl || `${cameraService.getCameraFullImageUrl(camera.id)}?t=${ts}`;
              link.href = href;
              link.download = `camera_${camera.id}_full.jpg`;
              link.click();
            }}
            disabled={!fullSnapshotUrl}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}; 